import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';

import { Icon } from 'js/components';

import {
  filterOrgPickerRowsByName,
  rowBreadcrumbItems,
  rowsByKey,
} from './flattenOrgPickerRows';
import OrgNodePickerRow from './OrgNodePickerRow';

import styles from './OrgNodePicker.module.scss';

function rowToLabel(row) {
  return row?.name ?? '';
}

function OrgNodePicker({
  rows = [],
  selectedRow = null,
  onSelect = () => {},
  disabled = false,
  className,
  toggleComponent: ToggleComponent = null,
}) {
  const searchInputRef = useRef(null);
  const isDisabled = disabled || rows.length === 0;
  const [searchQuery, setSearchQuery] = useState('');

  const rowLookup = useMemo(() => rowsByKey(rows), [rows]);

  const breadcrumbItems = useMemo(
    () => rowBreadcrumbItems(selectedRow, rowLookup),
    [selectedRow, rowLookup]
  );

  const handleBreadcrumbClick = useCallback(
    (row) => {
      if (row) {
        onSelect(row);
      }
    },
    [onSelect]
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return rows;
    }

    return filterOrgPickerRowsByName(rows, searchQuery);
  }, [rows, searchQuery]);

  const browseMode = !searchQuery.trim();

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredRows,
    selectedItem: selectedRow,
    itemToString: rowToLabel,
    inputValue: searchQuery,
    onInputValueChange: ({ inputValue }) => {
      setSearchQuery(inputValue ?? '');
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        onSelect(selectedItem);
      }
    },
    onIsOpenChange: ({ isOpen: open }) => {
      if (open) {
        setSearchQuery('');
      }
    },
    stateReducer(_state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.ToggleButtonClick:
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          return {
            ...changes,
            inputValue: '',
          };

        default:
          return changes;
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frameId = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [isOpen]);

  const toggleButtonProps = getToggleButtonProps({
    disabled: isDisabled,
  });

  const searchInputProps = getInputProps({
    ref: searchInputRef,
    placeholder: 'Search organization…',
    'aria-label': 'Search organization nodes',
  });

  const menuProps = getMenuProps();

  return (
    <div className={classNames(styles.root, className)}>
      {ToggleComponent ? (
        <ToggleComponent
          selectedRow={selectedRow}
          breadcrumbItems={breadcrumbItems}
          onBreadcrumbClick={handleBreadcrumbClick}
          toggleButtonProps={toggleButtonProps}
          isOpen={isOpen}
        />
      ) : (
        <div className={styles.control}>
          <button
            type="button"
            className={styles['toggle-button']}
            {...toggleButtonProps}
          >
            <h2 className={styles['node-name']}>
              {selectedRow?.name ?? 'Select a node'}
            </h2>
            <Icon
              icon={['far', isOpen ? 'chevron-up' : 'chevron-down']}
              fixedWidth
              // size="xs"
              className="icon icon-right"
            />
          </button>
        </div>
      )}
      <div
        className={classNames(styles.dropdown, {
          [styles['dropdown-open']]: isOpen,
        })}
      >
        <div className={styles.search}>
          <Icon icon={['far', 'search']} className={styles['search-icon']} />
          <input
            className={styles['search-input']}
            disabled={!isOpen || isDisabled}
            {...searchInputProps}
          />
        </div>
        <ul
          {...menuProps}
          className={classNames(styles.menu, {
            [styles['menu-tree']]: browseMode && isOpen,
            'org-mgmt-tree': browseMode && isOpen,
          })}
        >
          {!isOpen ? null : filteredRows.length === 0 ? (
            <li className={styles['empty-item']} key="no-results">
              {searchQuery.trim() ? 'No matching nodes' : 'No nodes available'}
            </li>
          ) : (
            filteredRows.map((row, index) => (
              <OrgNodePickerRow
                key={row.rowKey}
                row={row}
                mode={browseMode ? 'tree' : 'search'}
                highlighted={highlightedIndex === index}
                selected={selectedRow?.rowKey === row.rowKey}
                itemProps={getItemProps({ item: row, index })}
              />
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export default OrgNodePicker;
